import React from 'react';
import useCurrentUser from 'hooks/useCurrentUser';
import { ERoleCode } from 'types/enumItems';
import BillingPortalButton from 'components/BillingPortalButton';
import { BannerType, OrgListingBannerVo, ProviderListingBannerVo } from 'types/financials';
import useListingFeeStatus from './useListingFeeStatus';
import s from './s.module.less';

interface BannerContent {
    title: string;
    description: string;
    showPayButton: boolean;
}

const formatAmount = (cents: number) =>
    `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 })}`;

const feeNoun = (bannerType: BannerType) => {
    switch (bannerType) {
        case 'BOTH_OFFLINE':
            return 'lead-generation and pro-listing fees';
        case 'LEAD_GEN_OFFLINE':
            return 'lead-generation fee';
        default:
            return 'pro-listing fee';
    }
};

const getSoloContent = (banner: ProviderListingBannerVo): BannerContent => {
    const { bannerType, leadGenFailedCents, proListingFailedCents } = banner;
    switch (bannerType) {
        case 'BOTH_OFFLINE':
            return {
                title: 'Your listing is offline',
                description: `Your profile was removed from Klarity Health due to unpaid lead-generation and pro-listing fees totaling ${formatAmount(
                    leadGenFailedCents + proListingFailedCents,
                )}. Pay now to restore visibility to patients.`,
                showPayButton: true,
            };
        case 'LEAD_GEN_OFFLINE':
            return {
                title: 'Your listing is offline',
                description: `Your profile was removed from Klarity Health due to an unpaid lead-generation fee of ${formatAmount(
                    leadGenFailedCents,
                )}. Pay now to restore visibility to patients.`,
                showPayButton: true,
            };
        case 'PRO_LISTING_OFFLINE':
            return {
                title: 'Your listing is offline',
                description: `Your profile was removed from Klarity Health due to an unpaid pro-listing fee of ${formatAmount(
                    proListingFailedCents,
                )}. Pay now to restore visibility to patients.`,
                showPayButton: true,
            };
        case 'PRO_LISTING_WARNING':
        default:
            return {
                title: 'Your listing will be paused soon',
                description: `Your profile will be removed from Klarity Health due to an unpaid pro-listing fee of ${formatAmount(
                    proListingFailedCents,
                )}. Pay now to stay visible to patients.`,
                showPayButton: true,
            };
    }
};

const getTeamProviderContent = (banner: ProviderListingBannerVo): BannerContent => {
    const isWarning = banner.bannerType === 'PRO_LISTING_WARNING';
    return {
        title: isWarning ? 'Your listing will be paused soon' : 'Your listing is offline',
        description: `Your profile ${
            isWarning ? 'will not be visible to patients' : 'is not visible to new patients right now'
        } due to an unpaid ${feeNoun(banner.bannerType)}. Please contact your practice admin to ${
            isWarning ? 'stay visible to patients' : 'restore visibility'
        }.`,
        showPayButton: false,
    };
};

const OFFLINE_TYPES: BannerType[] = ['BOTH_OFFLINE', 'LEAD_GEN_OFFLINE', 'PRO_LISTING_OFFLINE'];

const feeBreakdown = (leadGenCents: number, proListingCents: number) => {
    const parts: string[] = [];
    if (leadGenCents > 0) {
        parts.push(`${formatAmount(leadGenCents)} in lead-generation fees`);
    }
    if (proListingCents > 0) {
        parts.push(`${formatAmount(proListingCents)} in pro-listing fees`);
    }
    return parts.join(' and ');
};

// Sum fees across a subset of affected providers (used for per-provider offline banners).
const providersFeeBreakdown = (providers: ProviderListingBannerVo[]) => {
    const leadGen = providers.reduce((sum, p) => sum + (p.leadGenFailedCents || 0), 0);
    const proListing = providers.reduce((sum, p) => sum + (p.proListingFailedCents || 0), 0);
    return feeBreakdown(leadGen, proListing);
};

const providerName = (provider?: ProviderListingBannerVo) =>
    [provider?.firstName, provider?.lastName].filter(Boolean).join(' ') || 'A provider';

// pro-listing 欠费超过 $2,000 才展示整店离线横幅
const ORG_PRO_LISTING_BANNER_THRESHOLD_CENTS = 2000 * 100;

const getOrgContent = (org: OrgListingBannerVo): BannerContent | null => {
    // Case 3 — whole practice offline. Still driven by orgListingPaused; uses org-level totals.
    if (org.orgListingPaused && org.orgProListingFailedCents > ORG_PRO_LISTING_BANNER_THRESHOLD_CENTS) {
        return {
            title: 'Your practice is offline',
            description: `All provider profiles were removed from Klarity Health due to unpaid fees: ${feeBreakdown(
                org.orgLeadGenFailedCents,
                org.orgProListingFailedCents,
            )}. Pay now to restore visibility to patients.`,
            showPayButton: true,
        };
    }

    // Everything else is derived from the affectedProviders list.
    const affected = org.affectedProviders || [];
    const offline = affected.filter((p) => OFFLINE_TYPES.includes(p.bannerType));
    const warning = affected.filter((p) => p.bannerType === 'PRO_LISTING_WARNING');

    // Case 4 — offline outranks warning: if any provider is offline, ignore warnings.
    if (offline.length > 0) {
        const breakdown = providersFeeBreakdown(offline);

        // Case 1 — single provider offline.
        if (offline.length === 1) {
            const name = providerName(offline[0]);
            return {
                title: `${name} is offline`,
                description: `${name}'s profile has been removed from Klarity Health due to unpaid fees: ${breakdown}. Pay now to restore visibility to patients.`,
                showPayButton: true,
            };
        }

        // Case 1.1 — multiple providers offline.
        return {
            title: `${offline.length} providers in your practice are offline`,
            description: `Some providers in your practice have been removed from Klarity Health due to unpaid fees: ${breakdown}. Pay now to restore visibility to patients.`,
            showPayButton: true,
        };
    }

    // Warning banner — pro-listing overdue but not yet paused. No dollar amounts per design.
    if (warning.length > 0) {
        // Case 2 — single provider warning.
        if (warning.length === 1) {
            const name = providerName(warning[0]);
            return {
                title: `${name} in your practice will be paused soon`,
                description: `${name} will be removed from Klarity Health due to unpaid pro-listing fees. Pay now to keep them visible to patients.`,
                showPayButton: true,
            };
        }

        // Case 2.2 — multiple providers warning.
        return {
            title: `${warning.length} providers in your practice will be paused soon`,
            description:
                'Some providers in your practice will be removed from Klarity Health due to unpaid pro-listing fees. Pay now to keep them visible to patients.',
            showPayButton: true,
        };
    }

    return null;
};

const ListingFeeBanner: React.FC = () => {
    const [currentUser] = useCurrentUser();
    const status = useListingFeeStatus();

    if (!currentUser || !status) {
        return null;
    }

    const { roleCode } = currentUser;
    let content: BannerContent | null = null;

    if (!status.groupPractice) {
        // Variant A — solo
        if (status.self && status.self.bannerType !== 'NONE') {
            content = getSoloContent(status.self);
        }
    } else if (roleCode === ERoleCode.GROUP_PROVIDER) {
        // Variant B — team provider: own status comes from status.self
        if (status.self && status.self.bannerType !== 'NONE') {
            content = getTeamProviderContent(status.self);
        }
    } else if (roleCode === ERoleCode.GROUP_OWNER || roleCode === ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT) {
        // Variants C/D — team owner / admin
        if (status.org && status.org.bannerType !== 'NONE') {
            content = getOrgContent(status.org);
        }
    }

    if (!content) {
        return null;
    }

    return (
        <div id="listing-fee-banner" className={s.wrap}>
            <div className={s.icon}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M8.25706 3.0976C9.02167 1.7383 10.9788 1.7383 11.7434 3.0976L17.3237 13.0181C18.0736 14.3514 17.1102 15.9987 15.5805 15.9987H4.4199C2.89025 15.9987 1.92682 14.3514 2.67675 13.0181L8.25706 3.0976ZM11.0001 12.9988C11.0001 13.5511 10.5524 13.9988 10.0001 13.9988C9.44784 13.9988 9.00012 13.5511 9.00012 12.9988C9.00012 12.4465 9.44784 11.9988 10.0001 11.9988C10.5524 11.9988 11.0001 12.4465 11.0001 12.9988ZM10.0001 4.99878C9.44784 4.99878 9.00012 5.44649 9.00012 5.99878V8.99878C9.00012 9.55106 9.44784 9.99878 10.0001 9.99878C10.5524 9.99878 11.0001 9.55106 11.0001 8.99878V5.99878C11.0001 5.44649 10.5524 4.99878 10.0001 4.99878Z"
                        fill="#FBBF24"
                    />
                </svg>
            </div>
            <div className={s.mid}>
                <div className={s.title}>{content.title}</div>
                <div className={s.des}>{content.description}</div>
            </div>
            {content.showPayButton && (
                <div className={s.right}>
                    <BillingPortalButton className={s.payNowBtn} text="Pay now →" type="text" hideIcon />
                </div>
            )}
        </div>
    );
};

export default ListingFeeBanner;
